import { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import type { IOptionChip } from '@components/Pages/AnalyticsPage/analytics.types';
import { formatTradeName } from '@components/Pages/AnalyticsPage/utils/professionFormatting';
import { CardIcon } from '@components/Pages/AnalyticsPage/CardIcon';
import { DateRangePicker } from '@components/ui/DateRangePicker/DateRangePicker';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import { PageLoader } from '@components/Pages/AnalyticsPage/Reports/shared/PageLoader';
import { ReportNotFoundState } from '@components/Pages/AnalyticsPage/Reports/shared/ReportNotFoundState';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ } from '@utils/dateUtils';
import { SummaryStats } from './components/SummaryStats';
import { WorkersHoursChart } from './components/WorkersHoursChart';
import { LocationDistribution } from './components/LocationDistribution';
import {
  invalidateContractorAnalysisSecondary,
  useContractorAnalysisData,
} from './hooks/useContractorAnalysisData';
import { computeDateRangeFromToday, initialWindow, matchPreset } from './helpers';
import {
  CONTRACTOR_RANGES,
  presetDays,
  type ContractorRange,
} from './types';
import styles from './styles.module.css';

const NAME_PLACEHOLDER = ' NAME ';
const TRADE_CHIP_FALLBACK_BG = '#576270';

/**
 * Route entry-point for the Contractor Analysis report. Owns only the date
 * window state + handlers + the trade chip derivation; everything else lives
 * in `useContractorAnalysisData`. No mock data: every number on screen comes
 * from trusstor's reports engine via the d-orca BFF.
 */
export function ContractorAnalysisReportPage() {
  const { t } = useTranslation();
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  const { siteId = '', reportId = '' } = useParams<{ siteId: string; reportId: string }>();
  const queryClient = useQueryClient();

  // Date window — source of truth for the analysis + locations queries.
  // `activePreset` tracks which 7d/14d/30d segment is highlighted; null when
  // the user picks a custom range. `useState` lazy initializer runs once at
  // mount with the project tz — matches trusstor's V2 default of
  // `today minus 6 days → today` inclusive.
  const [activePreset, setActivePreset] = useState<ContractorRange | null>('7d');
  const [{ dateFrom, dateTo }, setDateWindow] = useState(() => initialWindow(tz));

  const data = useContractorAnalysisData({ siteId, reportId, dateFrom, dateTo, tz });

  const rangeOptions = useMemo(
    () =>
      CONTRACTOR_RANGES.map((range) => ({
        id: range,
        label: t(`reports.contractorAnalysis.personnel.range${presetDays(range)}`),
      })),
    [t],
  );

  const handlePresetChange = useCallback(
    (next: ContractorRange) => {
      setActivePreset(next);
      setDateWindow(computeDateRangeFromToday(next, tz));
    },
    [tz],
  );

  const handleCustomRange = useCallback(
    (next: { dateFrom: string; dateTo: string }) => {
      setActivePreset(matchPreset(next.dateFrom, next.dateTo, tz));
      setDateWindow(next);
    },
    [tz],
  );

  const retrySecondaryQueries = useCallback(() => {
    invalidateContractorAnalysisSecondary(queryClient, siteId, data.professionId);
  }, [queryClient, siteId, data.professionId]);

  // Title fallback + trade-chip projection. Memoized so the props handed to
  // `SummaryStats` (which is `memo`-wrapped) keep stable references across
  // unrelated page re-renders.
  const professionLabel = useMemo(() => {
    const archived = data.record?.professionLabel;
    return archived && archived.length > 0
      ? archived
      : t('reports.contractorAnalysis.titleFallback');
  }, [data.record, t]);

  // Trade-card chip from the matching profession in the working set — same
  // `backgroundColor` + `tradeId` icon the dropdown chip uses. `null` while
  // the catalog is still loading or if the archived report references a
  // retired profession; SummaryStats falls back to a neutral chip.
  const tradeChip = useMemo<IOptionChip | null>(() => {
    if (!data.matchedProfession) return null;
    return {
      background: data.matchedProfession.backgroundColor ?? TRADE_CHIP_FALLBACK_BG,
      text: formatTradeName(data.matchedProfession.tradeId),
      tradeId: data.matchedProfession.tradeId,
    };
  }, [data.matchedProfession]);

  if (data.recordIsLoading)
    return <PageLoader ariaLabelKey="reports.contractorAnalysis.titleFallback" />;
  if (!data.record || !data.isContractorAnalysis)
    return (
      <ReportNotFoundState
        backHref={`/analytics/${siteId}`}
        notFoundKey="reports.contractorAnalysis.notFound"
        backKey="reports.contractorAnalysis.notFoundBack"
      />
    );

  // Title — bicolor layout: bold contractor name + light suffix.
  const rendered = t('reports.contractorAnalysis.title', { name: NAME_PLACEHOLDER });
  const [before, after] = rendered.split(NAME_PLACEHOLDER);

  return (
    <div className={styles.page}>
      {/* This page intentionally does NOT use the shared <ReportHeader>.
          Its header is structurally different from DR/OL/WT: a bicolor
          non-editable title (bold contractor name + light suffix) plus an
          embedded <SummaryStats> strip, all on the colored banner surface.
          The shared header assumes editable title + description nodes and a
          single-row date/right-slot layout. */}
      <header className={styles.banner}>
        <div className={styles.bannerInner}>
          <Link
            to={`/analytics/${siteId}`}
            className={styles.back}
            aria-label={t('reports.contractorAnalysis.back')}
          >
            ←
          </Link>
          <h1 className={styles.title}>
            {before}
            <span className={styles.titleName}>{professionLabel}</span>
            {after}
          </h1>
          <div className={styles.titleSpacer} aria-hidden="true" />
          <div className={styles.stats}>
            <SummaryStats
              tradeLabel={professionLabel}
              tradeChip={tradeChip}
              workingSinceLabel={data.workingSinceLabel}
              totalHours={Math.round(data.allTime.totalWorkHours)}
              avgWorkers={data.allTime.avgDailyWorkers}
              avgHours={data.allTime.avgDailyWorkHours}
            />
          </div>
        </div>
      </header>

      {data.hasSecondaryError && (
        <div className={styles.inlineErrorBanner} role="alert">
          <span>{t('reports.contractorAnalysis.error.bannerTitle')}</span>
          <button
            type="button"
            className={styles.retryButton}
            onClick={retrySecondaryQueries}
          >
            {t('reports.contractorAnalysis.error.retry')}
          </button>
        </div>
      )}

      <div className={styles.content}>
        <div className={styles.personnelHeader}>
          <h2 className={styles.personnelTitle}>
            <CardIcon name="helmet" size={18} />
            {t('reports.contractorAnalysis.personnel.title')}
          </h2>
          <SegmentedControl<ContractorRange>
            value={activePreset ?? '7d'}
            onChange={handlePresetChange}
            options={rangeOptions}
            ariaLabel={t('reports.contractorAnalysis.personnel.title')}
          />
          <DateRangePicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={handleCustomRange}
            tz={tz}
            ariaLabel={t('reports.contractorAnalysis.personnel.pickRange')}
          />
        </div>

        <div className={styles.body}>
          <WorkersHoursChart
            series={data.series}
            avgWorkers={+data.rangeAvgWorkers.toFixed(2)}
            avgHours={+data.rangeAvgHours.toFixed(2)}
            tz={tz}
          />
          <LocationDistribution
            activeLocations={data.activeLocations}
            totalHours={data.totalLocationsHours}
            rows={data.locationRows}
          />
        </div>
      </div>
    </div>
  );
}
